import { useEffect, useState } from 'react';
import { Modal, Space, Tabs } from 'antd';
import { WebApiModelsEmailTemplate } from '../../Repository/eventbookyapi';
import { useAuth } from '../../Contexts/AuthContext';
import { SubscriptionLevel } from '../../Types/SubscriptionLevel';
import { TextService } from '../../Services/TextService';
import { DeleteOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import Table, { ColumnsType } from 'antd/lib/table';
import Paragraph from 'antd/lib/typography/Paragraph';
import LeftMenuSettings from './LeftSettings';
import { EmailTemplateService } from '../../Services/EmailTemplateService';
import CreateEmailTemplateButton from './CreateEmailTemplateButton';
import EditEmailTemplate from './EditEmailTemplate';
import LayoutLeftMenu from '../../Components/Layout/LayoutLeftMenu';
import { IntegrationService } from '../../Services/IntegrationService';
import { IntegrationType } from '../../Types/IntegrationType';
import { mapPurposeToText } from '../../Mappers/mapAvailablePurpose';

const AdminEmailTemplatesAndAutomation = () => {
    const [templates, setTemplates] = useState<WebApiModelsEmailTemplate[]>([]);
    const [automationTemplates, setAutomationTemplates] = useState<WebApiModelsEmailTemplate[]>([]);
    const { user } = useAuth();
    const GetText = TextService.GetText;
    const { confirm } = Modal;
    const [enablePaymentLink, setEnablePaymentLink] = useState(false);

    useEffect(() => {
        IntegrationService.GetActiveIntegrations().then(result => {
            setEnablePaymentLink(!!result.find(p => p.type === IntegrationType.PingPayments));
        });
        reloadTemplates();
    }, []);

    const deleteTemplate = async (id: number) => {
        confirm({
            title: GetText('EmailTemplates_DeleteConfirm'),
            icon: <ExclamationCircleFilled />,
            content: GetText('EmailTemplates_DeleteConfirmMessage'),
            async onOk() {
                await EmailTemplateService.DeleteTemplate(id);
                reloadTemplates();
            },
            onCancel() {},
        });
    };

    const reloadTemplates = () => {
        EmailTemplateService.GetTemplates().then(result => {
            setTemplates(result.filter(t => !t.purpose));
            setAutomationTemplates(result.filter(t => !!t.purpose));
        });
    };

    const templateColumns: ColumnsType<WebApiModelsEmailTemplate> = [
        {
            title: GetText('Group_NameColumn'),
            dataIndex: 'uniqueId',
            key: 'uniqueId',
            render: (_, template) => <span><b>{template.templateName}</b></span>,
        },
        {
            title: GetText('MemberList_ActionColumn'),
            key: 'action',
            render: (_, template) => (
                <Space size="middle">
                    <EditEmailTemplate enablePaymentLink={enablePaymentLink} reloadTemplate={reloadTemplates} templates={templates} template={template}>Edit</EditEmailTemplate>
                    <DeleteOutlined style={{ color: 'red', fontSize: '20px' }} onClick={() => deleteTemplate(template.id!)} />
                </Space>
            ),
        },
    ];

    const automationColumns: ColumnsType<WebApiModelsEmailTemplate> = [
        {
            title: GetText('Group_NameColumn'),
            dataIndex: 'uniqueId',
            key: 'uniqueId',
            render: (_, template) => <span><b>{template.templateName}</b></span>,
        },
        {
            title: GetText('AutomaticSending_EventColumn'),
            dataIndex: 'purpose',
            key: 'purpose',
            render: (_, template) => <span>{mapPurposeToText(template.purpose)}</span>,
        },
        {
            title: GetText('MemberList_ActionColumn'),
            key: 'action',
            render: (_, template) => (
                <Space size="middle">
                    <EditEmailTemplate isAutomation enablePaymentLink={enablePaymentLink} reloadTemplate={reloadTemplates} templates={automationTemplates} template={template}>Edit</EditEmailTemplate>
                    <DeleteOutlined style={{ color: 'red', fontSize: '20px' }} onClick={() => deleteTemplate(template.id!)} />
                </Space>
            ),
        },
    ];

    const tabItems = [
        {
            key: 'templates',
            label: GetText('EmailTemplates_TemplateTitle'),
            children: (
                <>
                    <Paragraph>{GetText('EmailTemplates_Description')}</Paragraph>
                    {user?.subscriptionLevel === SubscriptionLevel.Free && templates.length === 1
                        ? <i>{GetText('EmailTemplates_UpgradeTo')} <a href='/Pricing'>Premium</a> {GetText('EmailTemplates_UpgradeTo2')}</i>
                        : <CreateEmailTemplateButton hidePurpose enablePaymentLink={enablePaymentLink} user={user} reloadTemplate={reloadTemplates} />
                    }
                    <Table columns={templateColumns} style={{ marginTop: '20px' }} locale={{ emptyText: GetText('EmailTemplates_NoGroupsCreated') }} className="admin-table" rowKey="Id" dataSource={templates} />
                </>
            ),
        },
        {
            key: 'automation',
            label: GetText('AutomaticSending_Title'),
            children: (
                <>
                    <Paragraph>{GetText('AutomaticSending_Description')}</Paragraph>
                    <CreateEmailTemplateButton isAutomation enablePaymentLink={enablePaymentLink} user={user} reloadTemplate={reloadTemplates} />
                    <Table columns={automationColumns} style={{ marginTop: '20px' }} locale={{ emptyText: GetText('AutomaticSending_NoItems') }} className="admin-table" rowKey="Id" dataSource={automationTemplates} />
                </>
            ),
        },
    ];

    return (
        <LayoutLeftMenu title={GetText('EmailTemplates_TemplateTitle')} menu={<LeftMenuSettings selected="emailTemplatesAndAutomation" />}>
            <Tabs defaultActiveKey="templates" items={tabItems} />
        </LayoutLeftMenu>
    );
};

export default AdminEmailTemplatesAndAutomation;
